// API Documentation and helper functions for backtesting

/*
### Strategy Endpoints

1. GET /strategies
   - Purpose: Get all strategies for authenticated user
   - Input: Optional strategyType query param
   - Output: Array of Strategy objects
   - Errors: 401 if not authenticated, 500 for server errors

2. GET /strategies/:id
   - Purpose: Get single strategy by ID
   - Input: id in URL
   - Output: Strategy object
   - Errors: 401 if not authenticated, 404 if not found

3. PATCH /strategies/:id
   - Purpose: Update strategy properties
   - Input: id in URL, name in body
   - Output: Updated Strategy object
   - Errors: 400 bad request, 401 unauthorized, 404 not found

### Ai Bot Builder Endpoints

1. GET /ai-bot-builder/list-versions?aiStrategyId=xxx  
   - Purpose: Retrieves the revision history for a given AI strategy
   - Input: aiStrategyId (string, required)
   - Output: Array of version objects with revision number, code, comments, etc.

2. POST /ai-bot-builder/generate-strategy
   - Purpose: Generates a new strategy
   - Input: user_query (string, required)
   - Output: Strategy ID, code, comments, revision number

3. POST /ai-bot-builder/refine-strategy
   - Purpose: Refines existing strategy
   - Input: aiStrategyId, update_prompt, current_code
   - Output: Updated strategy object with new revision

4. GET /ai-bot-builder/list-strategies
   - Purpose: Lists all AI strategies for authenticated user
   - Input: None (JWT auth required)
   - Output: Array of AiStrategy objects

5. GET /ai-bot-builder/usage-limits
   - Purpose: Gets usage information
   - Output: promptsUsed, maxPrompts, nextCreditAt, cycleStartDate

6. POST /ai-bot-builder/feedback/vote
   - Purpose: Records feedback vote
   - Input: revisionId, starRating (0-5)
   - Output: Feedback ID confirmation

7. PATCH /ai-bot-builder/feedback/description
   - Purpose: Updates feedback description
   - Input: feedbackId, description
   - Output: Confirmation message

8. GET /ai-bot-builder/feedback
   - Purpose: Gets user feedback
   - Input: Optional revisionId/aiStrategyId filters
   - Output: Array of feedback objects

### Backtest Endpoints

1. GET /backtests/:backtest_id/result
   - Purpose: Gets result files
   - Output: trades_html, trades_csv, tearsheet_html, stats_csv files

2. GET /backtests/:backtest_id/file/:file
   - Purpose: Gets specific result file
   - Input: backtest_id, file type
   - Output: File content

3. POST /backtests
   - Purpose: Starts new backtest
   - Input: bot_id, code, dates, config
   - Output: backtestId

4. POST /backtests/force-stop
   - Purpose: Stops running backtest
   - Input: backtest_id OR bot_id + revision_id
   - Output: Status confirmation

5. GET /backtests/:backtest_id/status
   - Purpose: Checks backtest status
   - Output: Status details and progress

6. GET /backtests/by-id/:backtest_id/stats
   - Purpose: Gets detailed stats
   - Output: Performance metrics

7. GET /backtests/check-completion
   - Purpose: Batch status check
   - Output: Status updates for all running backtests

8. GET /backtests/:strategy_id/stats
   - Purpose: Gets aggregated stats
   - Output: Strategy-wide performance metrics

9. GET /backtests/:strategy_id/results
   - Purpose: Gets all backtest results
   - Output: Array of result files

10. GET /backtests/:backtest_id/logs
    - Purpose: Gets execution logs
    - Output: Log content and status

### Deployment Endpoints (Partial - Add more as needed)

1. POST /deploy
   - Purpose: Starts a new deployment
   - Input: strategyId, aiStrategyRevisionId, bot_config
   - Output: deployId

2. GET /deploy/:deployId/status
   - Purpose: Checks deployment status
   - Output: Status details (status, error_details, etc.)

3. GET /deploy/:deployId/logs
   - Purpose: Gets deployment execution logs
   - Output: Log content and status

*/

export const formatters = {
  percent: (value) => {
    if (value === null || value === undefined) return "N/A";
    const percentValue = (value * 100).toFixed(2);
    // Add thousand separators to the percent value
    const parts = percentValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${parts.join('.')}%`;
  },
  number: (value) => {
    if (value === null || value === undefined) return "N/A";
    const numberValue = value.toFixed(2);
    // Add thousand separators
    const parts = numberValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  },
  date: (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  },
  dateTime: (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  },
  time: (seconds) => {
    if (seconds === null || seconds === undefined) return "N/A";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
};

// Ensure parseNumericValue is exported
export const parseNumericValue = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Ensure makePeriodKey is exported
export const makePeriodKey = (startDate, endDate) => {
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.getFullYear()}-${end.getFullYear()}`;
  } catch (err) {
    console.error("Error creating period key:", err);
    return "unknown";
  }
};

export async function startBacktest(token, params) {
  const { fetchWithAuth } = await import('./apiHelpers');
  const body = { ...(params || {}) };
  try {
    if (typeof window !== 'undefined') {
      const search = new URLSearchParams(window.location.search || '');
      const computeTier =
        search.get('compute_tier') ||
        search.get('computeTier') ||
        search.get('tier');
      if (computeTier && !body.compute_tier) {
        body.compute_tier = computeTier;
      }

      const computeTierMode =
        search.get('compute_tier_mode') ||
        search.get('computeTierMode') ||
        search.get('tier_mode');
      if (computeTierMode && !body.compute_tier_mode) {
        body.compute_tier_mode = computeTierMode;
      }
    }
  } catch {
    // Best-effort only; ignore URL parsing errors
  }
  return fetchWithAuth(token, "/backtests", {
    method: "POST",
    body
  });
}

export async function getBacktestStatus(token, backtestId) {
  const { fetchWithAuth } = await import('./apiHelpers');
  return fetchWithAuth(token, `/backtests/${backtestId}/status`);
}

export async function stopBacktest(token, params) {
  const { fetchWithAuth } = await import('./apiHelpers');
  return fetchWithAuth(token, "/backtests/force-stop", {
    method: "POST",
    body: params,
    // Node can wait 30 seconds for ECS confirmation before returning HTTP 202.
    // The ordinary 15-second fetch deadline would abort that acknowledgement.
    timeoutMs: 60_000
  });
}

const normalizedString = (value) => String(value || '').trim().toLowerCase();

const normalizedNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function normalizeBacktestResultStatus(backtestOrStatus) {
  if (!backtestOrStatus || typeof backtestOrStatus === 'string') {
    return normalizedString(backtestOrStatus);
  }

  const rawStatus = normalizedString(
    backtestOrStatus.status ||
      backtestOrStatus.statusRaw ||
      backtestOrStatus.status_raw ||
      backtestOrStatus.result?.status ||
      backtestOrStatus.metadata?.status
  );
  const resultClassification = normalizedString(
    backtestOrStatus.resultClassification ||
      backtestOrStatus.result_classification ||
      backtestOrStatus.metadata?.resultClassification ||
      backtestOrStatus.metadata?.result_classification
  );
  const firstCycleOutcome = normalizedString(
    backtestOrStatus.firstCycleOutcome ||
      backtestOrStatus.first_cycle_outcome ||
      backtestOrStatus.metadata?.firstCycleOutcome ||
      backtestOrStatus.metadata?.first_cycle_outcome
  );
  const tradeCountStatus = normalizedString(
    backtestOrStatus.tradeCountStatus ||
      backtestOrStatus.trade_count_status ||
      backtestOrStatus.metadata?.tradeCountStatus ||
      backtestOrStatus.metadata?.trade_count_status
  );
  const tradeCount = normalizedNumber(
    backtestOrStatus.tradeCount ??
      backtestOrStatus.trade_count ??
      backtestOrStatus.metadata?.tradeCount ??
      backtestOrStatus.metadata?.trade_count
  );

  if (
    (rawStatus === 'completed' || !rawStatus) &&
    (
      resultClassification === 'no_trades' ||
      firstCycleOutcome === 'no_trades' ||
      (tradeCountStatus === 'counted' && tradeCount === 0)
    )
  ) {
    return 'completed_no_trades';
  }

  return rawStatus;
}

const ACTIVE_BACKTEST_STATUSES = new Set([
  "initiated",
  "queued",
  "pending",
  "starting",
  "deploying",
  "running",
  "preparing",
  "processing",
  "in_progress",
  "in progress",
  "waiting_for_capacity",
  "downloading_data",
  "initializing",
  "backtesting",
  "building",
  "pulling",
  "stopping",
  "resuming",
  "connection issue",
]);

// Only statuses that the backend contract identifies as active should create
// polling, chart rendering, and local cache work. An unknown status remains
// visible in its record, but cannot create an unbounded browser work loop.
export function isBacktestRunning(status) {
  // Empty/null status means backtest just started and hasn't reported yet
  if (!status || String(status).trim() === '') {
    return true;
  }

  return ACTIVE_BACKTEST_STATUSES.has(String(status).trim().toLowerCase());
}
